import React from 'react';
import { connect } from 'react-redux';
import { Control, Form, actions } from 'react-redux-form';
import { Link } from 'react-router-dom';
import get from 'lodash/get';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';

import BaseDetailScreen from '../../BaseDetailScreen';
import LabeledRow from '../components/LabeledRow';
import InputRow from '../components/InputRow';
import InputField from '../../Forms/Controls/Field';
import CheckOption from '../components/CheckOption';
import { formatCurrency } from '../../Currency';
import Button from '../../Button';
import {
  baseOfferOrderPath,
  enumAnnualIncome,
  enumTotalNetWorth,
  enumLiquidNetWorth,
  offerOrderAmountPath,
  offerOrderDisclosuresPath,
  offerOrderTransferPath,
} from '../../Forms/Schemas/baseRrf';
import {
  fetchAccountCashBalanceWithOfferingOrders,
} from '../../../actions';
import Tooltip from '../../Tooltip';

import './styles.css';

const Big = require('big.js/big.js');


const getShareInc = ({
  pricePerShare,
  minimumInvestIncCurrencyAmt,
  minimumInvestShareIncrQty,
}) => {
  if (pricePerShare && minimumInvestIncCurrencyAmt) {
    return Math.max(
      Math.ceil(minimumInvestIncCurrencyAmt / pricePerShare),
      minimumInvestShareIncrQty,
    );
  }
  return minimumInvestShareIncrQty;
};

const RestrictedPersonCheckbox = props => (
  <CheckOption {...props}>
    By checking I am not considered a&nbsp;
    <Link
      styleName="disclaimerLink"
      to={`/invest/${props.offerId}/restricted-person`}
    >
      &quot;restricted person&quot;
    </Link>
    &nbsp;and am eligible to participate in this offering
  </CheckOption>
);


class Component extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      investmentOverAssets: false,
      requireUnderCheck: false,
      investmentOverLiqNetWorth: false,
    };

    this.overAssetRange = this.overAssetRange.bind(this);
    this.insideAssetRange = this.insideAssetRange.bind(this);
    this.overLiquidNetWorth = this.overLiquidNetWorth.bind(this);
  }

  async componentDidMount() {
    this.props.fetchAccountCashBalanceWithOfferingOrders(this.props.currentAccount);

    const { requestedDollarAmount } = get(this.props, offerOrderAmountPath);
    if (requestedDollarAmount) {
      await this.overAssetRange(requestedDollarAmount, this.props.hasMaximumInvest);
      this.insideAssetRange(requestedDollarAmount, this.props.hasMaximumInvest);
    }
  }

  async componentWillReceiveProps(newProps) {
    const newRDA = get(newProps, `${offerOrderAmountPath}.requestedDollarAmount`);
    const oldRDA = get(this.props, `${offerOrderAmountPath}.requestedDollarAmount`);

    if (newRDA !== oldRDA) {
      await this.overAssetRange(newRDA, this.props.hasMaximumInvest);
      this.insideAssetRange(newRDA, this.props.hasMaximumInvest);
    }
  }


  overLiquidNetWorth = (requestedDollarAmount) => {
    const liquidNetWorthMax =
      Big(enumLiquidNetWorth[this.props.finances.liquidNetWorth][3]);
    const over = Big(requestedDollarAmount).gt(liquidNetWorthMax);
    this.setState({ investmentOverLiqNetWorth: over });
    return over;
  }

  overAssetRange = async (requestedDollarAmount, hasMaximumInvest) => {
    const overLiquid = this.overLiquidNetWorth(requestedDollarAmount);
    if (hasMaximumInvest) {
      // get 10% of the asset values
      const annualIncomeMax =
      Big(enumAnnualIncome[this.props.finances.annualIncome][3]) * Big(0.1);
      const totalNetWorthMax =
        Big(enumTotalNetWorth[this.props.finances.totalNetWorth][3]) * Big(0.1);

      const over = requestedDollarAmount > annualIncomeMax &&
        requestedDollarAmount > totalNetWorthMax;

      await this.props.setValidity(!over && !overLiquid);
      this.setState({ investmentOverAssets: over });
    } else {
      await this.props.setValidity(!overLiquid);
      this.setState({ investmentOverAssets: false });
    }
  };

  insideAssetRange = (requestedDollarAmount, hasMaximumInvest) => {
    if (hasMaximumInvest) {
      const annualIncomeMin =
      Big(enumAnnualIncome[this.props.finances.annualIncome][2]) * Big(0.1);
      const annualIncomeMax =
        Big(enumAnnualIncome[this.props.finances.annualIncome][3]) * Big(0.1);

      const totalNetWorthMin =
        Big(enumTotalNetWorth[this.props.finances.totalNetWorth][2]) * Big(0.1);
      const totalNetWorthMax =
        Big(enumTotalNetWorth[this.props.finances.totalNetWorth][3]) * Big(0.1);

      let inside = false;
      const greaterMin = Math.max(annualIncomeMin, totalNetWorthMin);
      const greaterMax = Math.max(annualIncomeMax, totalNetWorthMax);
      if (requestedDollarAmount > greaterMin &&
        requestedDollarAmount <= greaterMax) {
        inside = true;
      }

      this.setState({ requireUnderCheck: inside });
    } else {
      this.setState({ requireUnderCheck: false });
    }
  };

  render() {
    const {
      cashBalance,
      offerOrderForm,
      offer,
      onNext,
      resetAll,
      setOfferId,
      pendingTransfers,
    } = this.props;
      // if a user loads a order form for a new offer then reset
      // the order form for the previous offer
    if (!offerOrderForm.offerId || (offerOrderForm.offerId !== offer.id)) {
      resetAll();
      setOfferId(offer.id);
    }

    const { isConditional } = this.props;
    const pageHeading = isConditional ? 'Conditional Order' : 'Order';

    const minShareQuantity = Math.ceil((offer.minimumInvestCurrencyAmt / offer.pricePerShare), 10);
    const shareIncrement = getShareInc(offer);
    const quantity = {
      model: '.quantity',
      RRFComp: Control,
      control: {
        component: InputRow,
        mask: createNumberMask({
          prefix: '',
          includeThousandsSeparator: true,
        }),
        changeAction: (model, value) => (dispatch) => {
          const cleanedVal = value.replace(/\D/g, '') || 0;
          dispatch(actions.change(model, cleanedVal));
          dispatch(actions.change(
            `${offerOrderAmountPath}.requestedDollarAmount`,
            (cleanedVal * offer.pricePerShare),
          ));
        },
        errors: {
          required: val => !val,
          noPartialShares: val => val && val.includes('.'),
          mininumInvestment: val => val && !val.includes('.') && !(val >= minShareQuantity),
          shareInc: (val) => {
            const amountAboveMinInvest = val - minShareQuantity;
            if (amountAboveMinInvest > 0 && amountAboveMinInvest % shareIncrement) {
              return true;
            }
            return false;
          },
        },
        mapProps: {
          label: 'Share Quantity',
          placeholder: '0',
          value: get(offerOrderForm, 'offerOrderAmount.quantity'),
        },
      },
      error: {
        messages: {
          required: 'Please enter an order quantity.',
          noPartialShares: 'Partial shares are not currently being accepted',
          mininumInvestment: `The minimum investment quantity is ${minShareQuantity}`,
          shareInc: `The investment quantity must be in increments of ${shareIncrement}`,
        },
      },
    };

    const totalInvestmentAmount = {
      model: '.totalInvestment',
      RRFComp: Control,
      control: {
        component: InputRow,
        mask: createNumberMask({
          prefix: '$',
          includeThousandsSeparator: true,
        }),
        changeAction: (model, value) => (dispatch) => {
          const cleanedVal = value.replace(/\D/g, '') || 0;
          dispatch(actions.change(model, cleanedVal));
          dispatch(actions.change(`${offerOrderAmountPath}.requestedDollarAmount`, cleanedVal));
        },
        errors: {
          required: val => !val,
          mininumInvestment: val => val && !val.includes('.') && !(val >= offer.minimumInvestCurrencyAmt),
        },
        placeholder: '0',
        mapProps: {
          label: 'Total Investment',
          placeholder: '0',
          value: get(offerOrderForm, 'offerOrderAmount.totalInvestment'),
        },
      },
      error: {
        messages: {
          required: 'Please enter total investment amount.',
          mininumInvestment: `The minimum investment amount is ${formatCurrency(offer.minimumInvestCurrencyAmt)}`,
        },
      },
    };

    const restrictedPerson = {
      model: '.unrestricted',
      RRFComp: Control.checkbox,
      control: {
        component: RestrictedPersonCheckbox,
        errors: {
          required: val => !val,
        },
        mapProps: {
          isToggle: true,
          offerId: offer.id,
        },
      },
      error: {
        messages: {
          required: 'Please confirm you are not restricted from investing in this offer.',
        },
      },
    };
    const underAssetCheck = {
      model: '.underAssets',
      RRFComp: Control.checkbox,
      control: {
        component: props => (
          <CheckOption
            styles={{ main: 'mainAlt' }}
            {...props}
          />
        ),
        errors: {
          required: val => !val,
        },
        mapProps: {
          isToggle: true,
          offerId: offer.id,
        },
      },
      error: {
        messages: {
          required: 'Please confirm your investment does not exceed your limit.',
        },
      },
    };

    const cashAvailableLabel = (
      <div styleName="cashAvailableLabel">
        <span>Cash Available</span>
        <Tooltip
          styles={{ display: 'inline-block', marginLeft: '8px' }}
          text="Cash available is the sum of your cash balance and pending transfers"
        />
      </div>
    );
    return (
      <BaseDetailScreen
        pageHeading={pageHeading}
        pageSubheading={offer.name}
        backTo={`/offers/${offer.id}`}
        backLabel="Offer Details"
      >
        <div styleName="main">
          <Form
            model={offerOrderAmountPath}
            onSubmit={onNext}
            styleName="amountForm"
          >
            <LabeledRow
              isCurrency
              label={cashAvailableLabel}
              value={(Number.parseFloat(cashBalance, 10) + pendingTransfers)}
            />
            <LabeledRow
              isCurrency
              label="Minimum Investment"
              value={offer.minimumInvestCurrencyAmt}
            />
            <div styleName="breakLinePadded" />
            {
              isConditional ?
                <LabeledRow
                  label="Share Range"
                  value={`${formatCurrency(offer.minSharePrice)} - ${formatCurrency(offer.maxSharePrice)}`}
                /> :
                <LabeledRow
                  isCurrency
                  label="Share Price"
                  value={offer.pricePerShare}
                />
            }
            {
              isConditional ?
                <div>
                  <div styleName="breakLineTotal" />
                  <InputField
                    key={totalInvestmentAmount.model}
                    fieldProps={totalInvestmentAmount}
                  />
                </div> :
                <InputField
                  key={quantity.model}
                  fieldProps={quantity}
                />
            }
            {
              !isConditional ?
                <div>
                  <div styleName="breakLineTotal" />
                  <LabeledRow
                    isCurrency
                    label="Total Investment"
                    value={get(offerOrderForm, 'offerOrderAmount.requestedDollarAmount')}
                  />
                </div> : null
            }
            { this.state.requireUnderCheck && !this.props.isAccredited ?
              <div>
                <InputField
                  key={underAssetCheck.model}
                  fieldProps={underAssetCheck}
                  id={underAssetCheck.model}
                >
                  By checking, I verify that the requested investment amount does not
                  exceed the greater of 10% of my total net worth or 10% of my
                  annual income.
                </InputField>
              </div>
              : null
            }
            { this.state.investmentOverAssets && !this.props.isAccredited ?
              <div styleName="errorText">
                The requested total investment amount exceeds 10% of your total net
                worth and 10% of your annual income. Please update the quantity or
                your financial information in&nbsp;
                <Link to={`/account/${this.props.currentAccount}`}>Account Management</Link>
                &nbsp;to make your investment valid.
              </div>
              : null
            }
            { this.state.investmentOverLiqNetWorth ?
              <div styleName="errorText">
                The requested total investment amount exceeds your liquid net
                worth. Please update the quantity or your financial information in&nbsp;
                <Link to={`/account/${this.props.currentAccount}`}>Account Management</Link>
                &nbsp;to make your investment valid.
              </div>
              : null
            }
            { offer.allowRestrictedPersons ?
              <div>
                <div styleName="breakLineTotal" />
                <InputField
                  key={restrictedPerson.model}
                  fieldProps={restrictedPerson}
                  id={restrictedPerson.model}
                />
              </div>
              : null
            }
            {
              isConditional ?
                <div styleName="conditionalDisclaimer">
                  {`This order will be automatically submitted if the final offering price is within the estimated share range of ${formatCurrency(offer.minSharePrice)} to ${formatCurrency(offer.maxSharePrice)}.`}
                </div> : null
            }
            <div styleName="submitButtonContainer">
              <Button type="submit">
                <div>Review Disclosures</div>
              </Button>
            </div>
          </Form>
        </div>
      </BaseDetailScreen>
    );
  }
}

Component.displayName = 'SelectInvestmentAmount';

const mapStateToProps = state => ({
  currentAccount: state.currentAccount,
  cashBalance: get(state.accountCashBalanceByAccount[state.currentAccount], 'data.balanceAvailableForT1Trades'),
  pendingTransfers: Number.parseFloat(get(state.accountCashBalanceByAccount[state.currentAccount], 'data.pendingTransfersAvailableForOfferings')),
  finances: get(state, 'user.data.finances'),
  isAccredited: get(state, 'isAccredited'),
});

const mapDispatchToProps = dispatch => ({
  resetAll: async () => {
    await dispatch(actions.reset(baseOfferOrderPath));
    await dispatch(actions.reset(offerOrderAmountPath));
    await dispatch(actions.reset(offerOrderDisclosuresPath));
    await dispatch(actions.reset(offerOrderTransferPath));
  },
  setValidity: async val => dispatch(actions.setValidity(`${offerOrderAmountPath}.requestedDollarAmount`, val)),
  setOfferId: async offerId => (dispatch(actions.change(`${baseOfferOrderPath}.offerId`, offerId))),
  fetchAccountCashBalanceWithOfferingOrders: (...args) =>
    (dispatch(fetchAccountCashBalanceWithOfferingOrders(...args))),
});

export default connect(mapStateToProps, mapDispatchToProps)(Component);



// WEBPACK FOOTER //
// ./src/components/Invest/InvestmentViews/SelectInvestmentAmount.js